import { Component, ElementRef, inject, OnDestroy, output, signal } from '@angular/core';
import { SettingsService } from '@app/_services/settings.service';
import { AlertService } from '@app/_services/alert.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { log } from '@app/_services/logger';

/**
 * Meo Audio Recording component.
 * Handles voice input capture, encoding to base64, and emitting results.
 * Includes Web Audio API AnalyserNode for reactive waveform visualization
 * and panel edge glow intensity control.
 */
@Component({
  selector: 'app-meo-audio',
  standalone: true,
  imports: [SvgComponent],
  templateUrl: './meo-audio.component.html',
  styleUrl: './meo-audio.component.scss',
})
export class MeoAudioComponent implements OnDestroy {
  readonly ss = inject(SettingsService);
  private readonly alertService = inject(AlertService);
  private readonly elementRef = inject(ElementRef);

  /** Emitted when recording is complete with base64 audio and format. */
  audioReady = output<{ base64: string; format: string }>();

  /** Emitted when recording is cancelled. */
  cancelled = output<void>();

  isRecording = signal(false);
  isProcessing = signal(false);
  elapsedSeconds = signal(0);

  /** Static array of 32 elements used only to render waveform bar DOM elements. */
  readonly waveformBarSlots = Array(48).fill(0);
  private waveformHistory: number[] = Array(48).fill(3); // amplitude history (scrolls R→L)
  private frameCounter = 0;

  /** Translation-backed text properties with fallbacks. */
  get listeningText(): string {
    return this.ss.t('meo_listening');
  }

  get processingText(): string {
    return this.ss.t('meo_processing');
  }

  get cancelLabel(): string {
    return this.ss.t('meo_cancel_recording');
  }

  get sendLabel(): string {
    return this.ss.t('meo_stop_and_send');
  }

  get formattedTime(): string {
    const total = this.elapsedSeconds();
    const mins = Math.floor(total / 60).toString().padStart(2, '0');
    const secs = (total % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }

  get maxTimeFormatted(): string {
    const mins = Math.floor(this.MAX_RECORDING_SECONDS / 60).toString().padStart(2, '0');
    const secs = (this.MAX_RECORDING_SECONDS % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }

  // ==================== MediaRecorder State ====================

  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordedMimeType = 'audio/webm';
  private timerIntervalId: ReturnType<typeof setInterval> | null = null;
  private wasCancelled = false;
  private readonly MAX_RECORDING_SECONDS = 60;

  // ==================== Web Audio API (Waveform + Glow) ====================

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationFrameId: number | null = null;
  private barElements: HTMLElement[] = [];
  private panelElement: HTMLElement | null = null;

  // ==================== Recording Lifecycle ====================

  async startRecording(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];

      const supportedMimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type));
      if (!supportedMimeType) {
        log.error('MeoAudio', 'No supported audio MIME types');
        await this.alertService.alert(
          this.ss.t('camera_notSupported_title'),
          this.ss.t('camera_notSupported_message'),
          this.ss.t('common_ok')
        );
        return;
      }

      this.recordedMimeType = supportedMimeType;
      this.recordedChunks = [];
      this.wasCancelled = false;
      this.isProcessing.set(false);

      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: supportedMimeType });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        // Cancel was already handled synchronously — skip async emission
        if (this.wasCancelled) return;

        this.isProcessing.set(true);
        const blob = new Blob(this.recordedChunks, { type: this.recordedMimeType });
        this.releaseMicStream();
        this.teardownAudioAnalyser();
        this.isRecording.set(false);
        this.clearTimer();

        // Convert to base64
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          const format = this.getFormatFromMimeType(this.recordedMimeType);
          this.isProcessing.set(false);
          this.audioReady.emit({ base64, format });
          log.info('MeoAudio', 'Recording complete', { size: blob.size, format });
        };
      };

      this.mediaRecorder.onerror = () => {
        log.error('MeoAudio', 'MediaRecorder error');
        this.isRecording.set(false);
        this.isProcessing.set(false);
        this.clearTimer();
        this.releaseMicStream();
        this.teardownAudioAnalyser();
      };

      this.mediaRecorder.start();
      this.isRecording.set(true);
      this.elapsedSeconds.set(0);
      this.startTimer();

      // Setup audio analyser for waveform visualization after a brief delay
      // to allow Angular to render the waveform bars DOM elements
      setTimeout(() => {
        if (!this.isRecording() || !this.mediaStream) return;
        this.setupAudioAnalyser(this.mediaStream);
      }, 50);

      log.info('MeoAudio', 'Recording started', { mimeType: supportedMimeType });
    } catch (error) {
      log.error('MeoAudio', 'Failed to start recording', error as Error);
      await this.alertService.alert(
        this.ss.t('micPermissionAfterDeny_title'),
        this.ss.t('micPermissionAfterDeny_content'),
        this.ss.t('common_ok')
      );
    }
  }

  stopRecording(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') return;
    this.mediaRecorder.stop();
  }

  cancelRecording(): void {
    this.wasCancelled = true;
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.recordedChunks = [];
    this.isRecording.set(false);
    this.isProcessing.set(false);
    this.clearTimer();
    this.releaseMicStream();
    this.teardownAudioAnalyser();
    this.cancelled.emit();
  }

  // ==================== Web Audio API (Waveform Visualization) ====================

  /**
   * Connect an AnalyserNode to the existing MediaStream for frequency data.
   * Caches DOM references once to avoid per-frame querySelector calls.
   */
  private setupAudioAnalyser(stream: MediaStream): void {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 64;        // 32 frequency bins — used for average amplitude
    this.analyser.smoothingTimeConstant = 0.8;  // prevents jitter

    const source = this.audioContext.createMediaStreamSource(stream);
    source.connect(this.analyser);
    // Do NOT connect to destination — we don't want to play back the mic

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    // Cache DOM references ONCE — avoids querySelectorAll per frame
    this.barElements = Array.from(
      this.elementRef.nativeElement.querySelectorAll('.waveform-bar')
    );
    this.panelElement = document.querySelector('.meo-panel-container');

    this.startVisualization();
  }

  /**
   * 60fps rAF loop — scrolling waveform that flows right-to-left.
   * Every 3 frames (~50ms), a new amplitude sample is pushed to the right end
   * and all values shift left. This creates a continuously scrolling waveform.
   */
  private startVisualization(): void {
    this.waveformHistory = Array(48).fill(3);
    this.frameCounter = 0;

    const update = () => {
      if (!this.analyser || !this.dataArray) return;

      this.analyser.getByteFrequencyData(this.dataArray);

      // Compute current average amplitude
      const avg = this.dataArray.reduce((a, b) => a + b, 0) / this.dataArray.length;
      const pct = Math.max(3, (avg / 255) * 100);

      // Shift waveform every 3 frames (~50ms) for smooth scrolling
      this.frameCounter++;
      if (this.frameCounter >= 3) {
        this.frameCounter = 0;
        this.waveformHistory.shift();
        this.waveformHistory.push(pct);
      }

      // Render history to bars via direct DOM
      for (let i = 0; i < this.barElements.length && i < this.waveformHistory.length; i++) {
        this.barElements[i].style.height = `${this.waveformHistory[i]}%`;
      }

      this.updateGlowIntensity(avg);
      this.animationFrameId = requestAnimationFrame(update);
    };
    this.animationFrameId = requestAnimationFrame(update);
  }

  /**
   * Drive the CSS --glow-intensity custom property on the panel container.
   * Fast attack (~16ms via rAF), slow decay handled by CSS transition on the ::after pseudo-element.
   */
  private updateGlowIntensity(amplitude: number): void {
    // amplitude is 0-255 average from AnalyserNode getByteFrequencyData
    const normalized = amplitude / 255;  // 0-1
    const intensity = 0.08 + (normalized * 0.25); // range: 0.08-0.33

    if (this.panelElement) {
      this.panelElement.style.setProperty('--glow-intensity', String(intensity));
    }
  }

  /**
   * Cleanup audio analyser, animation loop, and cached DOM references.
   */
  private teardownAudioAnalyser(): void {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.audioContext) this.audioContext.close();
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.barElements = [];
    this.waveformHistory = Array(48).fill(3);
    this.frameCounter = 0;
    // Reset glow intensity on panel before releasing reference
    if (this.panelElement) {
      this.panelElement.style.removeProperty('--glow-intensity');
    }
    this.panelElement = null;
  }

  // ==================== Private Helpers ====================

  private releaseMicStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  private startTimer(): void {
    this.clearTimer();
    this.timerIntervalId = setInterval(() => {
      const next = this.elapsedSeconds() + 1;
      this.elapsedSeconds.set(next);
      if (next >= this.MAX_RECORDING_SECONDS) {
        log.info('MeoAudio', 'Max recording duration reached, auto-stopping');
        this.stopRecording();
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerIntervalId !== null) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  private getFormatFromMimeType(mimeType: string): string {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('mp4')) return 'm4a';
    return 'webm';
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.releaseMicStream();
    this.teardownAudioAnalyser();
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;
  }
}
